'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { motion } from 'framer-motion';

interface Service {
    id: number;
    icon: string;
    title: string;
    description: string;
    features: string[];
    color: string;
    bg: string;
}

const MobileServicesConstellation = () => {
    // Exact same data as ServicesGrid to ensure "No Text Changes"
    const services: Service[] = [
        {
            id: 1,
            icon: 'MapPinIcon',
            title: 'Google Business Profile',
            description: 'Maximize your local visibility with expert GBP management and optimization strategies.',
            features: ['Profile setup', 'Keyword optimization', 'Review management'],
            color: 'text-blue-600',
            bg: 'bg-blue-100',
        },
        {
            id: 2,
            icon: 'MagnifyingGlassIcon',
            title: 'Strategic SEO',
            description: 'Drive organic traffic with data-driven SEO strategies tailored to your business goals.',
            features: ['SEO audit', 'On-page optimization', 'Technical SEO'],
            color: 'text-purple-600',
            bg: 'bg-purple-100',
        },
        {
            id: 3,
            icon: 'StarIcon',
            title: 'Reputation',
            description: 'Build trust and credibility with proactive online reputation monitoring and management.',
            features: ['Review monitoring', 'Response crafting', 'Sentiment analysis'],
            color: 'text-amber-600',
            bg: 'bg-amber-100',
        },
        {
            id: 4,
            icon: 'ChartBarIcon',
            title: 'Local SEO',
            description: 'Capture local customers with targeted geographic optimization and citation building.',
            features: ['Citation building', 'NAP consistency', 'Local link building'],
            color: 'text-emerald-600',
            bg: 'bg-emerald-100',
        },
        {
            id: 5,
            icon: 'DocumentTextIcon',
            title: 'Content Strategy',
            description: 'Engage your audience with SEO-optimized content that drives conversions.',
            features: ['Content calendar', 'Blog posts', 'Performance tracking'],
            color: 'text-rose-600',
            bg: 'bg-rose-100',
        },
        {
            id: 6,
            icon: 'PresentationChartLineIcon',
            title: 'Analytics',
            description: 'Make informed decisions with comprehensive analytics and actionable insights.',
            features: ['Custom dashboards', 'ROI measurement', 'Strategy sessions'],
            color: 'text-indigo-600',
            bg: 'bg-indigo-100',
        },
        {
            id: 7,
            icon: 'RocketLaunchIcon',
            title: 'Exponential Growth',
            description: 'The accumulation of all strategies leading to explosive brand authority and ROI.',
            features: ['Market Dominance', 'Revenue Scaling', 'Brand Authority'],
            color: 'text-yellow-500',
            bg: 'bg-yellow-100',
        }
    ];

    return (
        <div className="lg:hidden relative">
            {/* Vertical Connecting Line (Constellation backbone) */}
            <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-gradient-to-b from-slate-200 via-indigo-200 to-slate-200" />

            <div className="space-y-12 relative z-10 pb-8">
                {services.map((service, index) => (
                    <motion.div
                        key={service.id}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        className="relative pl-20 pr-4"
                    >
                        {/* Node Icon on Timeline */}
                        <div className="absolute left-4 top-0 -ml-4 flex items-center justify-center">
                            {/* Halo Effect */}
                            <div className={`absolute w-12 h-12 rounded-full ${service.bg} opacity-20 animate-pulse`} />
                            {/* Core Icon */}
                            <div className={`relative w-8 h-8 rounded-full bg-white border-2 ${service.bg.replace('bg-', 'border-')} shadow-sm flex items-center justify-center z-10`}>
                                <div className={`w-2 h-2 rounded-full ${service.bg.replace('bg-', 'bg-')}`} />
                            </div>
                        </div>

                        {/* Card Content */}
                        <div className="bg-white/60 backdrop-blur-md border border-white/50 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
                            <div className="flex items-start gap-4 mb-3">
                                <div className={`w-10 h-10 rounded-xl ${service.bg} flex items-center justify-center flex-shrink-0`}>
                                    <Icon name={service.icon} size={20} className={service.color} variant="solid" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 leading-tight mb-1">{service.title}</h3>
                                    <div className="h-0.5 w-8 bg-gradient-to-r from-slate-200 to-transparent rounded-full" />
                                </div>
                            </div>

                            <p className="text-slate-600 text-sm mb-4 leading-relaxed font-light">
                                {service.description}
                            </p>

                            <div className="flex flex-wrap gap-2">
                                {service.features.map((f, i) => (
                                    <span key={i} className="text-[10px] uppercase tracking-wider px-2 py-1 bg-slate-50 border border-slate-100/50 rounded-md text-slate-500 font-semibold">
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

export default MobileServicesConstellation;
